import type { DenialKind } from '../../../sdk/errors/apiError';
import { denialCopy, denialDetail, type DenialAction } from '../../../sdk/errors/denialCopy';

/**
 * O que o usuário vê quando o backend recusa a requisição.
 *
 * Uma única peça para as três situações em que a negativa aparece, porque o
 * texto e a saída são os mesmos e só o enquadramento muda (§1.1):
 *
 * | `variant` | onde                                              |
 * |-----------|---------------------------------------------------|
 * | `page`    | a rota inteira está bloqueada (guardas de rota)    |
 * | `panel`   | só a área de conteúdo (tabela admin que devolve 403)|
 * | `toast`   | corpo do `notification` de uma ação pontual        |
 *
 * Cor: família `warn` em todos os motivos. Vermelho é reservado a erro e ação
 * destrutiva (§7, §54) — ser barrado não é uma falha do usuário — e usar uma cor
 * só para a categoria inteira mantém o significado estável entre módulos (§25).
 *
 * Componente puramente visual (§45): as ações chegam como dados (`{ label, to }`)
 * vindos de `denialCopy`, e a navegação é `<a href>` por padrão. Quem estiver
 * dentro do router passa `onNavigate={navigate}` para não recarregar a página —
 * o toast, que é disparado de dentro do interceptor do axios, não tem router e
 * usa o link mesmo.
 *
 * O detalhe cru da API só aparece onde ele é a explicação de verdade
 * (`denialDetail`), nunca como despejo de erro do backend (§31).
 */
export interface AccessDeniedProps {
  kind: DenialKind;
  /** Mensagem crua da API. Exibida só quando acrescenta informação. */
  detail?: string;
  variant?: 'page' | 'panel' | 'toast';
  /** Quando faz sentido tentar de novo (ex.: 429), vira a ação primária. */
  onRetry?: () => void;
  /** Navegação SPA. Sem isso, os links são `<a href>` comuns. */
  onNavigate?: (to: string) => void;
  className?: string;
}

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

const BASE =
  'inline-flex h-10 items-center justify-center gap-2 rounded-input px-4 text-sm ' +
  'transition-colors duration-150 motion-reduce:transition-none';

const PRIMARY =
  'bg-brand font-semibold text-brand-contrast ' +
  'transition-[filter] hover:brightness-110';

const SECONDARY =
  'border border-edge bg-surface font-medium text-ink-secondary ' +
  'hover:border-edge-strong hover:bg-surface-hover hover:text-ink';

/** Tile do ícone, borda e fundo translúcidos como no resto do produto (§8). */
const TILE = 'inline-flex shrink-0 items-center justify-center rounded-card border border-warn/40 bg-warn/[0.12] text-warn-ink';

const SIZES = {
  page: { tile: 'h-14 w-14', icon: 24, title: 'text-2xl', gap: 'gap-4' },
  panel: { tile: 'h-12 w-12', icon: 20, title: 'text-lg', gap: 'gap-3' },
  toast: { tile: 'h-9 w-9', icon: 16, title: 'text-sm', gap: 'gap-3' },
} as const;

export default function AccessDenied({
  kind,
  detail,
  variant = 'page',
  onRetry,
  onNavigate,
  className,
}: AccessDeniedProps) {
  const copy = denialCopy[kind];
  const Icon = copy.icon;
  const size = SIZES[variant];
  const shownDetail = detail ? denialDetail(kind, detail) : undefined;
  // 'rate-limited' e 'readonly-key' não têm para onde mandar o usuário.
  const hasActions = Boolean(onRetry || copy.primary || copy.secondary);

  // No toast o botão é menor: ele divide espaço com a casca do notification.
  const compact = variant === 'toast' ? 'h-8 px-3 text-xs' : '';

  const link = (action: DenialAction, style: string) => (
    <a
      key={action.to + action.label}
      href={action.to}
      onClick={
        onNavigate
          ? (event) => {
              event.preventDefault();
              onNavigate(action.to);
            }
          : undefined
      }
      className={cx(BASE, style, FOCUS, compact)}
    >
      {action.label}
    </a>
  );

  // Tentar de novo assume a ação primária quando existe: é a saída mais direta
  // (§31), e a copy vira a alternativa. O toast mostra só a ação principal —
  // duas saídas num aviso de 6 segundos é ruído (§20).
  const actions = (
    <div
      className={cx(
        'flex gap-2',
        variant === 'toast'
          ? 'flex-wrap'
          : 'w-full flex-col sm:w-auto sm:flex-row sm:items-center',
        variant === 'page' ? 'sm:justify-center' : '',
      )}
    >
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cx(BASE, PRIMARY, FOCUS, compact)}
        >
          Tentar novamente
        </button>
      )}
      {copy.primary && link(copy.primary, onRetry ? SECONDARY : PRIMARY)}
      {variant !== 'toast' && copy.secondary && link(copy.secondary, SECONDARY)}
    </div>
  );

  if (variant === 'toast') {
    return (
      <div role="alert" className={cx('flex items-start', size.gap, className)}>
        <span className={cx(TILE, size.tile)} aria-hidden="true">
          <Icon size={size.icon} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={cx(size.title, 'font-semibold text-ink')}>{copy.title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-secondary">
            {copy.description}
          </p>
          {shownDetail && (
            <p className="mt-1 text-xs text-ink-muted">{shownDetail}</p>
          )}
          {hasActions && <div className="mt-3">{actions}</div>}
        </div>
      </div>
    );
  }

  const body = (
    <div className={cx('flex flex-col items-center text-center', size.gap)}>
      <span className={cx(TILE, size.tile)} aria-hidden="true">
        <Icon size={size.icon} />
      </span>

      <div className="max-w-md">
        <h2 className={cx(size.title, 'font-bold tracking-tight text-ink')}>{copy.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{copy.description}</p>
        {shownDetail && (
          <p className="mt-2 text-xs leading-relaxed text-ink-muted">{shownDetail}</p>
        )}
      </div>

      {hasActions && <div className="mt-2 w-full sm:w-auto">{actions}</div>}
    </div>
  );

  if (variant === 'panel') {
    return (
      <div
        className={cx(
          'rounded-card border border-edge bg-surface px-6 py-10 shadow-panel',
          className,
        )}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex min-h-[60vh] w-full items-center justify-center bg-canvas px-4 py-12',
        className,
      )}
    >
      {body}
    </div>
  );
}
